import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { collectDigestItems, searchDocumentAnchor, } from '@almanac/contracts';
import { renderBlock } from './primitives/registry.js';
const ORIGINAL_BLOCK_INDEX = '__almanacOriginalBlockIndex';
/**
 * The report's sections in render order (ascending `order`, default 0),
 * as `{ id, title }` pairs. Shared by {@link DigestView} and the digest page
 * shell so section-tab navigation and the rendered sections stay in sync.
 */
export function getDigestSections(report) {
    return Object.entries(report.sections)
        .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
        .map(([id, section]) => ({ id, title: section.title }));
}
/**
 * Mirror lines carry no feedback policy of their own; they inherit the
 * canonical item's. A canonical item that is missing or not feedback-enabled
 * yields `allowed_feedback: []`, so the mirror renders no controls.
 */
function withCanonicalFeedback(item, canonicalById) {
    if (item.item_id || !item.mirrors_item_id)
        return item;
    const canonical = canonicalById.get(item.mirrors_item_id);
    const allowed = canonical?.feedback_enabled ? (canonical.allowed_feedback ?? undefined) : [];
    return { ...item, allowed_feedback: allowed };
}
/** Formats an ISO timestamp as a short local time, e.g. `5:04p`. */
function formatTime(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const meridiem = hours < 12 ? 'a' : 'p';
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour12}:${minutes}${meridiem}`;
}
/**
 * Renders a full {@link DigestReport} — the M1 full-fidelity render path.
 * Iterates sections (respecting `order`) then blocks, mapping each block to
 * the shared primitive components via {@link renderBlock} (a switch on
 * `block.type`). Does not go through json-render's spec format at all.
 *
 * Each `<section>` carries a `section-${id}` anchor (with `scroll-mt` to clear
 * a sticky header) so a page shell can offer section-tab navigation.
 *
 * `overlay`, keyed by `item_id`, threads local feedback state (done /
 * not relevant / snoozed) down into `item_list` blocks so items reflect
 * their current status without mutating the stored report.
 *
 * `showTitle` (default true) renders the report title + meta row. Page shells
 * that already surface the title in a header pass `false` to avoid duplication.
 */
export function DigestView({ report, overlay, showTitle = true, highlightedAnchor, onFeedback, pendingFeedback, }) {
    const sections = getDigestSections(report);
    const canonicalById = new Map();
    for (const { item } of collectDigestItems(report)) {
        if (item.item_id)
            canonicalById.set(item.item_id, item);
    }
    return (_jsxs("div", { id: searchDocumentAnchor(report.date, ''), className: "scroll-mt-36 space-y-10", children: [showTitle && report.title ? (_jsxs("header", { className: "space-y-2", children: [_jsx("h1", { className: "font-display text-3xl font-semibold tracking-tight text-foreground", children: report.title }), _jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [_jsxs("span", { className: "tabular-nums", children: ["Generated ", formatTime(report.generated_at)] }), report.sources.map((source) => (_jsx("span", { className: "rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground", children: source }, source)))] })] })) : null, sections.map(({ id, title }) => (_jsxs("section", { id: `section-${id}`, "aria-label": title, className: "scroll-mt-32 space-y-3", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { "aria-hidden": true, className: "h-4 w-[3px] flex-none rounded-full bg-primary" }), _jsx("h2", { className: "font-display text-lg font-semibold text-foreground", children: title }), _jsx("span", { "aria-hidden": true, className: "h-px flex-1 bg-border" })] }), _jsx("div", { className: "space-y-3", children: report.sections[id]?.blocks.map((block, index) => {
                            const originalIndex = block[ORIGINAL_BLOCK_INDEX] ?? index;
                            const pointer = `/sections/${id}/blocks/${originalIndex}`;
                            const anchor = searchDocumentAnchor(report.date, pointer);
                            return (_jsx("div", { id: anchor, "data-highlighted": highlightedAnchor === anchor ? '' : undefined, className: "scroll-mt-36 data-highlighted:rounded-xl data-highlighted:outline-2 data-highlighted:outline-offset-4 data-highlighted:outline-accent", children: renderBlock(block.type === 'item_list'
                                    ? {
                                        ...block,
                                        items: block.items.map((item) => withCanonicalFeedback(item, canonicalById)),
                                    }
                                    : block, overlay, anchor, highlightedAnchor, onFeedback, pendingFeedback) }, pointer));
                        }) })] }, id)))] }));
}
